import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GPX_FEATURES } from '@rsos/gainsight';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { RootState } from '../../../../app';
import isTextBasedSource from '../../../../utils/isSourceSMS';
import AllMessages from '../../../PreconfiguredMessages/AllMessages';
import { hasRequestLocationMessage } from '../../../PreconfiguredMessages/AllMessages/utils';
import { useEnabledPreconfiguredMessages } from '../../../PreconfiguredMessages/useEnabledPreconfiguredMessages';
import { useChatContext } from '../ChatContext';
import { TF911Container, AbsoluteAllMessagesContainer } from './TF911.styles';
import TF911Chat from './TF911Chat';
import { trackCloseAllMessagesModal } from './TF911ChatBoxTrackings';
import TF911ChatHeader from './TF911ChatHeader';
import { useTF911Conversation } from './useTF911Conversation';

const TF911ChatBox = () => {
  const {
    isCollapsed: isChatboxCollapsed,
    isAllMessagesVisible,
    setIsAllMessagesVisible,
    onCopyToChat,
    onSendMessage,
    locationRequestPathway,
  } = useChatContext();

  const selectedCallerID = useSelector(
    (state: RootState) => state.emergencyCalls.selectedCallerID,
  );

  const orgName = useSelector(
    (state: RootState) =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole?.application ?? '',
      )?.name,
  );

  const {
    getOrStartConversation,
    conversationID,
    isConversationEnded,
    isOutboundSMSConversation,
    isConversationMetadataReady,
  } = useTF911Conversation();

  const selectedCall = useSelectedCall();
  const mostRecentLocation = getMostRecentLoc(selectedCall);

  const isTextBasedOrigin = Boolean(
    isOutboundSMSConversation || isTextBasedSource(mostRecentLocation?.source),
  );

  const enabledPreconfiguredMessages =
    useEnabledPreconfiguredMessages(orgName, {
      hasRequestLocation: isTextBasedOrigin,
    }) || [];

  const hasRequestLocation =
    isTextBasedOrigin &&
    hasRequestLocationMessage(enabledPreconfiguredMessages);

  const handleViewClose = () => {
    setIsAllMessagesVisible(false);
    trackCloseAllMessagesModal(conversationID ?? '');
  };

  const handleStartConversation = useCallback(
    async (message?: string) => {
      return getOrStartConversation(message);
    },
    [getOrStartConversation],
  );

  const showStartChat = useMemo(() => {
    return !conversationID || isConversationEnded;
  }, [conversationID, isConversationEnded]);

  const flexValue = useMemo(() => {
    if (isChatboxCollapsed) {
      return '0 0 auto';
    }
    return '1 1 100%';
  }, [isChatboxCollapsed]);

  return (
    <TF911Container data-name="text-from-911-container" flex={flexValue}>
      <TF911ChatHeader
        onStartConversation={handleStartConversation}
        showStartChat={showStartChat}
        gpxFeature={
          isOutboundSMSConversation
            ? GPX_FEATURES.TF911_OUTBOUND
            : GPX_FEATURES.TF911
        }
      />

      {isConversationMetadataReady && selectedCallerID && (
        <TF911Chat
          callerID={selectedCallerID}
          preconfiguredMessages={enabledPreconfiguredMessages ?? []}
          hasRequestLocationAccess={hasRequestLocation}
        />
      )}
      {isAllMessagesVisible && (
        <AbsoluteAllMessagesContainer>
          <AllMessages
            preconfiguredMessages={enabledPreconfiguredMessages}
            onClose={handleViewClose}
            onCopyToChat={onCopyToChat}
            onSendMessage={onSendMessage}
            pathwayType={locationRequestPathway}
          />
        </AbsoluteAllMessagesContainer>
      )}
    </TF911Container>
  );
};

export default TF911ChatBox;
